import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import {
    Paper, Grid, TextField, MenuItem, Button, Typography, Box, Autocomplete, Divider, FormControl, InputLabel, Select, Checkbox, ListItemText, IconButton, Popover, Chip, Switch, Tooltip
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { usePlano } from "../../contexts/PlanContext";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import bnccData from "./BNCC/bncc.json";

import useMediaQuery from "@mui/material/useMediaQuery";

const tiposConteudo = [
    { value: "Atividade", label: "Atividade" },
    { value: "Prova", label: "Avaliação" },
    { value: "Plano de Aula", label: "Plano de Aula" }
];

const etapasEnsino = [
    /*"Infantil",*/ "Fundamental I", "Fundamental II", "Ensino Médio"
];

const seriesPorEtapa = {
    "Infantil": ["1º período", "2º período", "3º período"],
    "Fundamental I": ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano"],
    "Fundamental II": ["6º ano", "7º ano", "8º ano", "9º ano"],
    "Ensino Médio": ["1ª série", "2ª série", "3ª série"]
};

const niveis = [
    "Básico", "Intermediário", "Avançado"
];

const tiposQuestao = [
    { value: "Dissertativa", label: "Dissertativa" },
    { value: "Múltipla escolha", label: "Múltipla escolha" },
    { value: "Verdadeiro ou falso", label: "Verdadeiro ou Falso" },
    { value: "Ilustrativa", label: "Ilustrativa" }
];

const disciplinasPorEtapa = {
    "Infantil": ["Linguagem Oral", "Matemática", "Natureza e Sociedade", "Movimento", "Música", "Artes Visuais"],
    "Fundamental I": ["Língua Portuguesa", "Matemática", "Ciências", "História", "Geografia", "Artes", "Educação Física", "Inglês"],
    "Fundamental II": ["Língua Portuguesa", "Matemática", "Ciências", "História", "Geografia", "Artes", "Educação Física", "Inglês"],
    "Ensino Médio": ["Língua Portuguesa", "Matemática", "Física", "Química", "Biologia", "História", "Geografia", "Filosofia", "Sociologia", "Artes", "Educação Física", "Inglês"]
};

const GENERO_TEXTUAL_MAX_LENGTH = 25;
const BNCC_CODIGO_MAX_LENGTH = 12;
const BNCC_DESCRICAO_MAX_LENGTH = 300;
const BNCC_MAX_GROUPS = 5;
const OBSERVACOES_GERAIS_MAX_LENGTH = 100;

const FormDocument = forwardRef(function FormDocument({ onGerarDocumento, disabled }, ref) {
    const { user } = useAuth();
    const { planoStatus } = usePlano();
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();

    const maxQtd = planoStatus === "G" ? 5 : 10;

    // Campos do formulário
    const [tipo_conteudo, setTipoConteudo] = useState("");
    const [etapa_ensino, setEtapaEnsino] = useState("");
    const [serie, setSerie] = useState("");
    const [disciplina, setDisciplina] = useState("");
    const [tema, setTema] = useState("");
    const [nivel, setNivel] = useState("");
    const [tipo_questao, setTipoQuestao] = useState([]);
    const [quantidade_questoes, setQuantidadeQuestoes] = useState('');

    // Interpretação de texto
    const [interpretacao_texto, setInterpretacaoTexto] = useState(false);
    const [genero_textual, setGeneroTextual] = useState("");

    // Produção textual
    const [producao_textual, setProducaoTextual] = useState(false);
    const [tipo_producao_textual, setTipoProducaoTextual] = useState("");

    // Informativo do texto-base no mobile
    const isTouch = useMediaQuery("(pointer: coarse)");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [openHelp, setOpenHelp] = useState(false);


    // Observações Gerais (opcional)
    const [obsOpen, setObsOpen] = useState(false);
    const [observacoes_gerais, setObservacoesGerais] = useState("");

    // Preferências do usuário (3 primeiros campos)
    const PREF_KEY = useMemo(() => `docen_form_prefs_v1:${user?.email || "anon"}`, [user?.email]);

    const [prefs, setPrefs] = useState(null);
    const [showPrefsSuggestion, setShowPrefsSuggestion] = useState(false);

    // carrega prefs ao montar / trocar usuário
    useEffect(() => {
        try {
            const raw = localStorage.getItem(PREF_KEY);
            const parsed = raw ? JSON.parse(raw) : null;

            if (
                parsed &&
                typeof parsed === "object" &&
                parsed.tipo_conteudo &&
                parsed.etapa_ensino &&
                parsed.serie
            ) {
                setPrefs(parsed);
                setShowPrefsSuggestion(true); // mostra só ao entrar/refresh
            } else {
                setPrefs(null);
                setShowPrefsSuggestion(false);
            }
        } catch {
            setPrefs(null);
            setShowPrefsSuggestion(false);
        }
    }, [PREF_KEY]);

    const persistPrefs = (next) => {
        try {
            localStorage.setItem(PREF_KEY, JSON.stringify(next));
            setPrefs(next);
        } catch {
            // silencioso
        }
    };

    const applyPrefs = () => {
        if (!prefs) return;

        setTipoConteudo(prefs.tipo_conteudo);
        setEtapaEnsino(prefs.etapa_ensino);
        setDisciplina("");
        setSerie(prefs.serie);

        setShowPrefsSuggestion(false);
    };

    const shouldShowPrefs = !!prefs && showPrefsSuggestion;

    /*--------------------------------------------------------------------------------------------*/

    // BNCC (lista final)
    const [bncc, setBncc] = useState([]); // [{ codigo, descricao }]

    // Inputs manuais
    const [bnccCodigo, setBnccCodigo] = useState("");
    const [bnccDescricao, setBnccDescricao] = useState("");

    // Erros/helps
    const [bnccCodigoError, setBnccCodigoError] = useState(false);
    const [bnccDescricaoError, setBnccDescricaoError] = useState(false);
    const [bnccCodigoHelper, setBnccCodigoHelper] = useState("");
    const [bnccDescricaoHelper, setBnccDescricaoHelper] = useState("");

    // Validação do código (ex.: EF06MA07)
    // const isBnccCode = (s) => /^[A-Z]{2}\d{2}[A-Z]{2}\d{2}$/.test(String(s).trim());

    // Sugestões para BNCC
    const getBnccCodesOptions = () => {
        if (!etapa_ensino || !serie || !disciplina) return [];
        const etapaObj = bnccData.find(e => e.etapa === etapa_ensino);
        if (!etapaObj) return [];

        let codigos = new Set();

        if (etapa_ensino === "Infantil") {
            const anoObj = (etapaObj.anos || []).find(a => a.ano === serie);
            (anoObj?.campos_experiencia || []).forEach(c =>
                (c.habilidades || []).forEach(h => codigos.add(h.codigo?.toUpperCase()))
            );
        } else {
            const anoObj = (etapaObj.anos || []).find(a => a.ano === serie);
            const discObj = (anoObj?.disciplinas || []).find(d => d.disciplina === disciplina);
            (discObj?.habilidades || []).forEach(h => codigos.add(h.codigo?.toUpperCase()));
        }
        return Array.from(codigos).filter(Boolean).sort();
    };

    const bnccCodesOptions = getBnccCodesOptions();

    const findBnccByCode = (code) => {
        const codigo = String(code || "").trim().toUpperCase();

        if (!codigo || !etapa_ensino || !serie || !disciplina) return null;

        const etapaObj = bnccData.find(e => e.etapa === etapa_ensino);
        if (!etapaObj) return null;

        const anoObj = (etapaObj.anos || []).find(a => a.ano === serie);
        if (!anoObj) return null;

        if (etapa_ensino === "Infantil") {
            for (const campo of anoObj.campos_experiencia || []) {
                const habilidade = (campo.habilidades || []).find(
                    h => String(h.codigo || "").toUpperCase() === codigo
                );

                if (habilidade) return habilidade;
            }

            return null;
        }

        const discObj = (anoObj.disciplinas || []).find(d => d.disciplina === disciplina);
        if (!discObj) return null;

        return (discObj.habilidades || []).find(
            h => String(h.codigo || "").toUpperCase() === codigo
        ) || null;
    };

    const preencherDescricaoBnccPorCodigo = (code) => {
        const habilidade = findBnccByCode(code);

        if (!habilidade?.descricao) return false;

        setBnccDescricao(String(habilidade.descricao || "").slice(0, BNCC_DESCRICAO_MAX_LENGTH));
        setBnccDescricaoError(false);
        setBnccDescricaoHelper("");

        return true;
    };

    // Popover da lista
    const [anchorBncc, setAnchorBncc] = useState(null);
    const openBncc = Boolean(anchorBncc);
    const handleOpenBncc = (e) => setAnchorBncc(e.currentTarget);
    const handleCloseBncc = () => setAnchorBncc(null);

    // Ações
    const addBncc = () => {
        if (bncc.length >= BNCC_MAX_GROUPS) {
            setBnccCodigoError(true);
            setBnccCodigoHelper(`Limite de ${BNCC_MAX_GROUPS} BNCCs atingido.`);
            return;
        }

        const codigo = bnccCodigo.trim().toUpperCase().slice(0, BNCC_CODIGO_MAX_LENGTH);
        let descricao = bnccDescricao.trim().slice(0, BNCC_DESCRICAO_MAX_LENGTH);

        if (!descricao) {
            const habilidade = findBnccByCode(codigo);
            descricao = String(habilidade?.descricao || "").slice(0, BNCC_DESCRICAO_MAX_LENGTH);
        }

        let ok = true;
        if (!codigo) {
            setBnccCodigoError(true); setBnccCodigoHelper("Informe o código.");
            ok = false;
        }
        if (!descricao) {
            setBnccDescricaoError(true); setBnccDescricaoHelper("Informe a descrição.");
            ok = false;
        }
        if (!ok) return;

        // evita duplicado exato
        if (bncc.some(i => i.codigo === codigo && i.descricao === descricao)) {
            setBnccCodigoError(true); setBnccCodigoHelper("BNCC já incluída.");
            return;
        }

        setBncc(prev => [...prev, { codigo, descricao }]);
        setBnccCodigo(""); setBnccDescricao("");
        setBnccCodigoError(false); setBnccCodigoHelper("");
        setBnccDescricaoError(false); setBnccDescricaoHelper("");
    };

    const removeBncc = (idx) => {
        setBncc(prev => {
            const next = prev.filter((_, i) => i !== idx);
            if (next.length === 0) handleCloseBncc();
            return next;
        });
    };


    const bnccCountLabel = `${bncc.length}/${BNCC_MAX_GROUPS} BNCC ${bncc.length === 1 ? "incluída" : "incluídas"}`;

    /*--------------------------------------------------------------------------------------------*/

    // Reseta o formulário
    useImperativeHandle(ref, () => ({
        reset: () => {
            setTipoConteudo("");
            setEtapaEnsino("");
            setSerie("");
            setDisciplina("");
            setTema("");
            setNivel("");
            setTipoQuestao([]);
            setQuantidadeQuestoes("");

            setInterpretacaoTexto(false);
            setGeneroTextual("");

            setProducaoTextual(false);
            setTipoProducaoTextual("");

            setBncc([]);
            setBnccCodigo("");
            setBnccDescricao("");

            setObsOpen(false);
            setObservacoesGerais("");

        }
    }));

    useEffect(() => {
        setBncc([]); // limpa lista ao trocar filtros
        setBnccCodigo(""); setBnccDescricao("");
        setBnccCodigoError(false); setBnccDescricaoError(false);
        setBnccCodigoHelper(""); setBnccDescricaoHelper("");
    }, [etapa_ensino, serie, disciplina]);

    // Ao sair de Língua Portuguesa, limpa apenas os campos exclusivos da produção textual
    useEffect(() => {
        if (disciplina !== "Língua Portuguesa") {
            setGeneroTextual("");
            setProducaoTextual(false);
            setTipoProducaoTextual("");
        }

        setOpenHelp(false);
    }, [disciplina]);

    // Quando ativar produção textual, limpa os tipos tradicionais e a quantidade
    useEffect(() => {
        if (producao_textual) {
            setTipoQuestao([]);
            setQuantidadeQuestoes("");
        } else {
            setTipoProducaoTextual("");
        }
    }, [producao_textual]);


    // Opções dinâmicas
    const seriesDisponiveis = etapa_ensino ? seriesPorEtapa[etapa_ensino] || [] : [];
    const disciplinasDisponiveis = etapa_ensino ? disciplinasPorEtapa[etapa_ensino] || [] : [];

    const isPlanoAula = tipo_conteudo === "Plano de Aula";

    const isLinguaPortuguesa = disciplina === "Língua Portuguesa";
    const mostrarGeneroTextual = isLinguaPortuguesa && !isPlanoAula && (interpretacao_texto || producao_textual);
    const mostrarTipoProducaoTextual = isLinguaPortuguesa && !isPlanoAula && producao_textual;
    const mostrarQuantidadeQuestoes = !isPlanoAula && !producao_textual;

    const handleToggleInterpretacao = (checked) => {
        setInterpretacaoTexto(checked);

        if (checked) {
            setProducaoTextual(false);
            setTipoProducaoTextual("");
        }
    };

    const handleToggleProducaoTextual = (checked) => {
        setProducaoTextual(checked);

        if (checked) {
            setInterpretacaoTexto(false);
            setTipoQuestao([]);
            setQuantidadeQuestoes("");
        } else {
            setTipoProducaoTextual("");
        }
    };

    // Submit
    const handleSubmit = (e) => {
        e.preventDefault();
        // Monta objeto de envio (não inclui tipo_questao se for Plano de Aula)
        const dados = {
            tipo_conteudo,
            etapa_ensino,
            serie,
            disciplina,
            interpretacao_texto,
            producao_textual,
            tema,
            bncc,
            nivel
        };

        if (!isPlanoAula && !producao_textual) {
            dados.tipo_questao = tipo_questao;
            dados.quantidade_questoes = quantidade_questoes;
        }

        if (!isPlanoAula && producao_textual) {
            dados.tipo_producao_textual = tipo_producao_textual;
            dados.quantidade_questoes = 1; // compatibilidade com o backend
        }

        if (mostrarGeneroTextual && genero_textual.trim()) {
            dados.genero_textual = genero_textual.trim();
        }

        if (observacoes_gerais.trim()) dados.observacoes_gerais = observacoes_gerais.trim();

        if (dados.bncc.length > 0) {

            // garante salvar o padrão mais recente
            if (tipo_conteudo && etapa_ensino && serie) {
                persistPrefs({ tipo_conteudo, etapa_ensino, serie });
            }


            //console.log(dados)
            onGerarDocumento(dados);
        } else {
            showSnackbar("Insira pelo menos uma norma da BNCC.", "warning");
        }
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" mb={2}>Gerar Documento Pedagógico</Typography>

            {shouldShowPrefs && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        border: (t) => `1px dashed ${t.palette.primary.main}`,
                        display: "inline-flex",      // não ocupa 100%
                        alignItems: "center",
                        gap: 2,                      // sem space-between
                        flexWrap: "wrap",
                        width: "fit-content",
                        maxWidth: "100%",
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 200, lineHeight: 1.2 }}>
                        {!isMobile ? "Padrão recente: " : ""}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {prefs?.tipo_conteudo} • {prefs?.etapa_ensino} • {prefs?.serie}
                        </Box>
                    </Typography>

                    <Button
                        size="small"
                        variant="contained"
                        onClick={applyPrefs}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 800,
                            minWidth: 0,
                            px: 1.2,
                            py: 0.4,
                            fontSize: 12,
                            lineHeight: 1.1,
                        }}
                    >
                        Aplicar
                    </Button>
                </Box>

            )}


            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            label="Tipo de Conteúdo"
                            value={tipo_conteudo}
                            onChange={e => {
                                setTipoConteudo(e.target.value);
                                setTipoQuestao([]);
                                setShowPrefsSuggestion(false);
                            }}
                            fullWidth
                            required
                            disabled={disabled}
                        >
                            {tiposConteudo.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            select
                            label="Etapa de Ensino"
                            value={etapa_ensino}
                            onChange={e => {
                                setEtapaEnsino(e.target.value);
                                setSerie("");
                                setDisciplina("");
                                setShowPrefsSuggestion(false);
                            }}
                            fullWidth
                            required
                            disabled={disabled}
                        >
                            {etapasEnsino.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            select
                            label="Série/Ano"
                            value={serie}
                            onChange={e => {
                                setSerie(e.target.value);
                                setShowPrefsSuggestion(false);
                            }}
                            fullWidth
                            required
                            disabled={disabled || !etapa_ensino}
                        >
                            {seriesDisponiveis.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>


                    {/* DISCIPLINA */}
                    <Grid item xs={12} sm={3}>
                        <TextField
                            select
                            label="Disciplina"
                            value={disciplina}
                            onChange={e => setDisciplina(e.target.value)}
                            fullWidth
                            required
                            disabled={disabled || !etapa_ensino}
                        >
                            {disciplinasDisponiveis.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Interpretação / Produção Textual em Língua Portuguesa | Texto-base nas demais disciplinas */}
                    {!isPlanoAula && (
                        <Grid item xs={12} sm={isLinguaPortuguesa ? 2 : 2}>
                            {isLinguaPortuguesa ? (
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        gap: 0.1
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }}>
                                        <Switch
                                            size="small"
                                            checked={interpretacao_texto}
                                            onChange={(e) => handleToggleInterpretacao(e.target.checked)}
                                            disabled={disabled}
                                        />

                                        <Typography variant="body2">
                                            Interpretação
                                        </Typography>

                                        <Tooltip
                                            title="Ao marcar, a questão 1 trará um texto-base e as demais questões serão relacionadas a esse texto."
                                            arrow
                                            open={isTouch ? openHelp : undefined}
                                            onClose={isTouch ? () => setOpenHelp(false) : undefined}
                                            disableHoverListener={isTouch}
                                            disableFocusListener={isTouch}
                                            disableTouchListener={!isTouch}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.25, ml: 0.3 }}
                                                onClick={isTouch ? () => setOpenHelp(v => !v) : undefined}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }}>
                                        <Switch
                                            size="small"
                                            checked={producao_textual}
                                            onChange={(e) => handleToggleProducaoTextual(e.target.checked)}
                                            disabled={disabled}
                                        />

                                        <Typography variant="body2">
                                            Prod. Textual
                                        </Typography>
                                        <Tooltip
                                            title="Ao marcar, terá uma descrição do texto que o aluno deverá produzir e a área para a escrita."
                                            arrow
                                            open={isTouch ? openHelp : undefined}
                                            onClose={isTouch ? () => setOpenHelp(false) : undefined}
                                            disableHoverListener={isTouch}
                                            disableFocusListener={isTouch}
                                            disableTouchListener={!isTouch}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.25, ml: 0.3 }}
                                                onClick={isTouch ? () => setOpenHelp(v => !v) : undefined}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0,
                                        height: "100%"
                                    }}
                                >
                                    <Switch
                                        size="medium"
                                        checked={interpretacao_texto}
                                        onChange={(e) => setInterpretacaoTexto(e.target.checked)}
                                        disabled={disabled}
                                    />

                                    <Typography variant="body1">
                                        Texto-base
                                    </Typography>

                                    <Tooltip
                                        title="Ao marcar, a questão 1 trará um texto-base e as demais questões serão relacionadas a esse texto."
                                        arrow
                                        open={isTouch ? openHelp : undefined}
                                        onClose={isTouch ? () => setOpenHelp(false) : undefined}
                                        disableHoverListener={isTouch}
                                        disableFocusListener={isTouch}
                                        disableTouchListener={!isTouch}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ p: 0.25 }}
                                            onClick={isTouch ? () => setOpenHelp(v => !v) : undefined}
                                        >
                                            <HelpOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Grid>
                    )}

                    {/* Tema + Gênero Textual */}
                    {isLinguaPortuguesa ? (
                        <>
                            {mostrarGeneroTextual && (
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Gênero Textual"
                                        value={genero_textual}
                                        onChange={e => setGeneroTextual(e.target.value.slice(0, GENERO_TEXTUAL_MAX_LENGTH))}
                                        required={mostrarGeneroTextual}
                                        fullWidth
                                        disabled={disabled}
                                        placeholder="Ex.: poema, conto, fábula, notícia..."
                                        inputProps={{
                                            maxLength: GENERO_TEXTUAL_MAX_LENGTH,
                                        }}
                                        helperText={`${genero_textual.length}/${GENERO_TEXTUAL_MAX_LENGTH} caracteres`}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={mostrarGeneroTextual ? 4 : 7}>
                                <TextField
                                    label="Tema (assunto central)"
                                    value={tema}
                                    onChange={e => setTema(e.target.value)}
                                    required
                                    fullWidth
                                    disabled={disabled}
                                    placeholder="Ex.: amizade, família, meio ambiente..."
                                    inputProps={{
                                        maxLength: 60,
                                    }}
                                    helperText={`${tema.length}/60 caracteres`}
                                />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} sm={!isPlanoAula ? 7 : 5}>
                            <TextField
                                label="Tema (assunto central)"
                                value={tema}
                                onChange={e => setTema(e.target.value)}
                                required
                                fullWidth
                                disabled={disabled}
                                placeholder="Ex.: frações, célula animal, Revolução Francesa..."
                                inputProps={{
                                    maxLength: 60,
                                }}
                                helperText={`${tema.length}/60 caracteres`}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            label="Nível de Dificuldade"
                            value={nivel}
                            onChange={e => setNivel(e.target.value)}
                            fullWidth
                            required
                            disabled={disabled}
                        >
                            {niveis.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {!isPlanoAula && (
                        <>
                            <Grid item xs={12} sm={4}>
                                {mostrarTipoProducaoTextual ? (
                                    <TextField
                                        select
                                        label="Tipo de Questão"
                                        value={tipo_producao_textual}
                                        onChange={(e) => setTipoProducaoTextual(e.target.value)}
                                        fullWidth
                                        required
                                        disabled={disabled}
                                    >
                                        <MenuItem value="Descrição com imagem">Descrição com imagem</MenuItem>
                                        <MenuItem value="Descrição textual">Descrição textual</MenuItem>
                                    </TextField>
                                ) : (
                                    <FormControl fullWidth required disabled={disabled}>
                                        <InputLabel>Tipo de Questão</InputLabel>
                                        <Select
                                            label="Tipo de Questão"
                                            multiple
                                            value={tipo_questao}
                                            onChange={e => setTipoQuestao(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                            renderValue={selected =>
                                                selected.map(val => tiposQuestao.find(opt => opt.value === val)?.label || val).join(', ')
                                            }
                                            MenuProps={{
                                                PaperProps: { style: { maxHeight: 280, width: 250 } }
                                            }}
                                        >
                                            {tiposQuestao.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value} sx={{ paddingTop: 0.5, paddingBottom: 0.5 }}>
                                                    <Checkbox checked={tipo_questao.indexOf(opt.value) > -1} />
                                                    <ListItemText primary={opt.label} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>

                            {mostrarQuantidadeQuestoes && (
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        select
                                        label="Quantidade de Questões"
                                        value={quantidade_questoes}
                                        onChange={(e) => setQuantidadeQuestoes(Number(e.target.value))}
                                        fullWidth
                                        required
                                        disabled={disabled}
                                    >
                                        {Array.from({ length: 10 }, (_, i) => {
                                            const v = i + 1;
                                            return (
                                                <MenuItem key={v} value={v} disabled={v > maxQtd}>
                                                    {v}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Grid>
                            )}


                        </>
                    )}

                    {etapa_ensino && serie && disciplina ? (

                        <>

                            <Grid item xs={12}>
                                <Divider variant="middle" sx={{ my: 1 }}><Chip label="BNCC" size="small" color="primary" /></Divider>
                            </Grid>

                            <Grid item xs={12}>

                                <Grid container spacing={1} alignItems="flex-start">
                                    {/* Código (com sugestões somente de códigos) */}
                                    <Grid item xs={12} md={3}>
                                        <Autocomplete
                                            freeSolo
                                            options={bnccCodesOptions}
                                            inputValue={bnccCodigo}
                                            onInputChange={(e, v) => {
                                                const upper = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, BNCC_CODIGO_MAX_LENGTH);
                                                setBnccCodigo(upper);

                                                if (bnccCodigoError) {
                                                    setBnccCodigoError(false);
                                                    setBnccCodigoHelper("");
                                                }

                                                preencherDescricaoBnccPorCodigo(upper);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Código BNCC"
                                                    error={bnccCodigoError}
                                                    helperText={bnccCodigoHelper || `${bnccCodigo.length}/${BNCC_CODIGO_MAX_LENGTH} caracteres • Ex.: EF06MA07`}
                                                    placeholder="EF06MA07"
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        maxLength: BNCC_CODIGO_MAX_LENGTH,
                                                        style: { textTransform: "uppercase", letterSpacing: 1 }
                                                    }}
                                                    onBlur={() => {
                                                        const v = bnccCodigo.trim().toUpperCase();

                                                        if (v) {
                                                            preencherDescricaoBnccPorCodigo(v);
                                                        }
                                                    }}
                                                    disabled={disabled}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Descrição */}
                                    <Grid item xs={12} md={7}>
                                        <TextField
                                            label="Descrição BNCC"
                                            value={bnccDescricao}
                                            onChange={(e) => {
                                                setBnccDescricao(e.target.value.slice(0, BNCC_DESCRICAO_MAX_LENGTH));
                                                if (bnccDescricaoError) { setBnccDescricaoError(false); setBnccDescricaoHelper(""); }
                                            }}
                                            onBlur={() => {
                                                if (!bnccDescricao.trim()) {
                                                    setBnccDescricaoError(true); setBnccDescricaoHelper("Informe a descrição.");
                                                }
                                            }}
                                            fullWidth
                                            error={bnccDescricaoError}
                                            helperText={bnccDescricaoHelper || `${bnccDescricao.length}/${BNCC_DESCRICAO_MAX_LENGTH} caracteres • Ex.: Resolver problemas de frações.`}
                                            disabled={disabled}
                                            placeholder="Digite a descrição da BNCC"
                                            multiline
                                            maxRows={3}
                                            inputProps={{
                                                maxLength: BNCC_DESCRICAO_MAX_LENGTH,
                                            }}
                                        />
                                    </Grid>

                                    {/* Adicionar */}
                                    <Grid item xs={12} md={2}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={addBncc}
                                            disabled={disabled || bncc.length >= BNCC_MAX_GROUPS}
                                            fullWidth
                                            sx={{ height: 56 }}
                                        >
                                            Add BNCC
                                        </Button>
                                    </Grid>

                                    {/* Contador + popover com lista e remover */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                            <Chip
                                                label={bnccCountLabel}
                                                color="primary"
                                                variant="outlined"
                                                onClick={bncc.length ? handleOpenBncc : undefined}
                                                sx={{ cursor: bncc.length ? "pointer" : "default" }}
                                            />
                                        </Box>

                                        {openBncc && bncc.length > 0 && (
                                            <Popover
                                                open={openBncc && bncc.length > 0}
                                                anchorEl={anchorBncc}
                                                onClose={handleCloseBncc}
                                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                PaperProps={{ sx: { p: 1.5, maxWidth: 500, maxHeight: 320, overflowY: "auto", borderRadius: 2 } }}
                                            >
                                                {bncc.map((item, idx) => (
                                                    <Box
                                                        key={`${item.codigo}-${idx}`}
                                                        sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, px: 1, borderBottom: "1px solid #eee" }}
                                                    >
                                                        <Typography variant="body2" color="primary" sx={{ fontWeight: 700, minWidth: 92 }}>{item.codigo}</Typography>
                                                        <Typography variant="body2" sx={{ flex: 1 }}>{item.descricao}</Typography>
                                                        <IconButton title="Remover BNCC" size="small" onClick={() => removeBncc(idx)}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Popover>
                                        )}

                                    </Grid>
                                </Grid>

                            </Grid>

                            <Grid item xs={12}>
                                <Divider variant="middle" sx={{ my: 1 }} />
                            </Grid>

                            {/* Observações Gerais (opcional) */}
                            <Grid item xs={6}>
                                <Box
                                    onClick={() => setObsOpen(v => !v)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        mt: 1
                                    }}
                                >
                                    <IconButton size="small" disabled={disabled}>
                                        {obsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>

                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Observações gerais (opcional)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        (instruções extras para a IA)
                                    </Typography>
                                </Box>

                                {obsOpen && (
                                    <Box sx={{ mt: 1 }}>
                                        <TextField
                                            label="Observações gerais"
                                            value={observacoes_gerais}
                                            onChange={(e) => setObservacoesGerais(e.target.value.slice(0, OBSERVACOES_GERAIS_MAX_LENGTH))}
                                            fullWidth
                                            disabled={disabled}
                                            placeholder='Ex.: "Quero que o texto contenha a palavra estável com o grafema “p”" | "Use gráficos nas questões ilustrativas"'
                                            multiline
                                            minRows={2}
                                            maxRows={3}
                                            inputProps={{
                                                maxLength: OBSERVACOES_GERAIS_MAX_LENGTH,
                                            }}
                                            helperText={`${observacoes_gerais.length}/${OBSERVACOES_GERAIS_MAX_LENGTH} caracteres`}
                                        />
                                    </Box>
                                )}
                            </Grid>

                        </>

                    ) : (
                        <></>
                    )}

                </Grid>
                <Box mt={3} display="flex" justifyContent="flex-end">
                    {disabled ? (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                // mantém uma “área” parecida com a do botão, mas sem ocupar a linha toda
                                minHeight: 36,
                                px: 9,
                            }}
                        >
                            <AutoAwesomeIcon
                                sx={{
                                    color: "primary.main",
                                    fontSize: 45,
                                    "@keyframes docenSparkle": {
                                        "0%": { transform: "scale(1)" },
                                        "50%": { transform: "scale(1.12)" },
                                        "100%": { transform: "scale(1)" },
                                    },

                                    animation: "docenSparkle 0.9s ease-in-out infinite",
                                }}
                            />
                        </Box>
                    ) : (
                        <Button size="large" type="submit" variant="contained" color="primary" disabled={disabled} fullWidth={isMobile} sx={{ borderRadius: 1 }}>
                            <AutoAwesomeIcon sx={{ color: "#FFF", fontSize: 20, mr: 1 }} />
                            Gerar Documento
                        </Button>
                    )}

                </Box>
            </form>
        </Paper>
    );
});

export default FormDocument;